import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  Alert,
  Checkbox,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  CheckBoxOutlineBlank,
  CheckBox,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";
import { tokens } from "../../theme";
import { addGroup, editGroup, fetchAllGroups } from "../../store/actions";

const UserGroups = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAllGroups());
  }, [dispatch]);

  const state = useSelector((state) => state);
  const groups = useSelector((state) => state?.admin?.userGroups?.list || []);
  const users = useSelector((state) => state?.admin?.rooms?.list || []);

  const [openModal, setOpenModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [addUserModal, setAddUserModal] = useState(null);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    status: "public",
    start_time: "",
    users: [],
  });

  useEffect(() => {
    if (addUserModal) {
      setSelectedUsersToAdd([]);
    }
  }, [addUserModal]);

  const handleOpenModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        name: group.name,
        status: group.status,
        start_time:
          group.start_time.split("T")[0] +
          "T" +
          group.start_time.split("T")[1].slice(0, 5),
        users: group.users,
      });
    } else {
      setEditingGroup(null);
      setFormData({
        name: "",
        status: "public",
        start_time: "",
        users: [],
      });
    }
    setOpenModal(true);
  };

  const availableUsers = users.filter(
    (user) =>
      !addUserModal?.users.some((existingUser) => existingUser._id === user._id)
  );

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingGroup(null);
  };

  const handleSubmit = () => {
    if (editingGroup) {
      // setGroups(groups.map((g) => (g.id === editingGroup.id ? groupData : g)));
    } else {
      dispatch(addGroup(formData));
    }

    handleCloseModal();
  };

  const handleDeleteGroup = (groupId) => {
    // setGroups(groups.filter((g) => g.id !== groupId));
    setDeleteConfirm(null);
  };

  const handleRemoveUserFromGroup = (groupId, userId) => {
    // setGroups(
    //   groups.map((group) =>
    //     group.id === groupId
    //       ? {
    //           ...group,
    //           users: group.users.filter((user) => user.id !== userId),
    //         }
    //       : group
    //   )
    // );
  };

  const handleAddUsersToGroup = (groupId, newUsers) => {
    dispatch(editGroup({ groupId: groupId, users: newUsers }));

    setAddUserModal(null);
  };

  const getStatusColor = (status) => {
    return status === "public"
      ? colors.greenAccent[500]
      : colors.redAccent[500];
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString();
  };

  const icon = <CheckBoxOutlineBlank fontSize="small" />;
  const checkedIcon = <CheckBox fontSize="small" />;

  return (
    <Box m="20px">
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb="30px"
      >
        <Box>
          <Typography
            variant="h2"
            color={colors.grey[100]}
            fontWeight="bold"
            sx={{ mb: "5px" }}
          >
            User Groups
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            Manage user groups and permissions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: "14px",
            fontWeight: "bold",
            padding: "12px 24px",
            borderRadius: "12px",
            textTransform: "none",
            boxShadow: `0 4px 14px ${colors.blueAccent[800]}`,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
              transform: "translateY(-2px)",
              boxShadow: `0 8px 25px ${colors.blueAccent[800]}`,
            },
            "&:active": {
              transform: "translateY(0px)",
            },
          }}
        >
          Add Group
        </Button>
      </Box>

      {/* Conditional Rendering: No Groups or Groups Grid */}
      {!groups || groups.length === 0 ? (
        /* No Groups Display */
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          sx={{
            backgroundColor: colors.primary[400],
            borderRadius: "16px",
            border: `1px dashed ${colors.grey[600]}`,
            padding: "40px",
            textAlign: "center",
          }}
        >
          <GroupIcon
            sx={{
              fontSize: 80,
              color: colors.grey[600],
              mb: 2,
              opacity: 0.5,
            }}
          />
          <Typography
            variant="h4"
            color={colors.grey[300]}
            fontWeight="bold"
            mb={1}
          >
            No Groups Found
          </Typography>
          <Typography
            variant="body1"
            color={colors.grey[400]}
            mb={3}
            maxWidth="400px"
          >
            You haven't created any groups yet. Click the "Add Group" button
            above to create your first group.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "12px 24px",
              borderRadius: "12px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: colors.blueAccent[600],
              },
            }}
          >
            Create Your First Group
          </Button>
        </Box>
      ) : (
        /* Groups Grid */
        <Grid container spacing={3}>
          {groups.map((group, index) => (
            <Grid
              item
              xs={12}
              md={6}
              lg={4}
              key={group.id}
              sx={{
                animation: `slideInUp 0.6s ease-out ${index * 0.1}s both`,
                "@keyframes slideInUp": {
                  "0%": {
                    opacity: 0,
                    transform: "translateY(30px)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              <Card
                sx={{
                  backgroundColor: colors.primary[400],
                  border: `1px solid ${colors.grey[700]}`,
                  height: "100%",
                  borderRadius: "16px",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 20px 40px ${colors.primary[800]}`,
                    border: `1px solid ${colors.blueAccent[500]}`,
                    "&::before": {
                      opacity: 1,
                    },
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${colors.blueAccent[900]}20, ${colors.greenAccent[900]}10)`,
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                    zIndex: 0,
                  },
                  "& > *": {
                    position: "relative",
                    zIndex: 1,
                  },
                }}
              >
                <CardContent>
                  {/* Group Header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Box display="flex" alignItems="center">
                      <GroupIcon
                        sx={{ color: colors.blueAccent[500], mr: 1 }}
                      />
                      <Typography
                        variant="h5"
                        color={colors.grey[100]}
                        fontWeight="bold"
                      >
                        {group.name}
                      </Typography>
                    </Box>
                    <Box>
                      <Tooltip title="Edit Group">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenModal(group);
                          }}
                          sx={{
                            color: colors.grey[300],
                            backgroundColor: `${colors.grey[700]}30`,
                            borderRadius: "10px",
                            padding: "8px",
                            marginRight: "8px",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: colors.blueAccent[700],
                              color: colors.grey[100],
                              transform: "scale(1.1)",
                            },
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Group">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm(group);
                          }}
                          sx={{
                            color: colors.grey[300],
                            backgroundColor: `${colors.grey[700]}30`,
                            borderRadius: "10px",
                            padding: "8px",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: colors.redAccent[600],
                              color: colors.grey[100],
                              transform: "scale(1.1)",
                            },
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Group Status */}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Box display="flex" alignItems="center">
                      <Chip
                        label={group.status.toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(group.status),
                          color: colors.grey[100],
                          fontWeight: "bold",
                          fontSize: "11px",
                          height: "24px",
                          borderRadius: "12px",
                          "& .MuiChip-label": {
                            padding: "0 8px",
                          },
                        }}
                      />
                    </Box>
                    <Box display="flex" alignItems="center">
                      <BusinessIcon
                        sx={{ color: colors.grey[400], mr: 0.5, fontSize: 14 }}
                      />
                      <Typography
                        variant="caption"
                        color={colors.grey[400]}
                        fontWeight="500"
                      >
                        {group.organizationId}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Start Time */}
                  <Box
                    display="flex"
                    alignItems="center"
                    mb={2}
                    sx={{
                      backgroundColor: `${colors.grey[800]}20`,
                      borderRadius: "8px",
                      padding: "8px 12px",
                    }}
                  >
                    <ScheduleIcon
                      sx={{
                        color: colors.blueAccent[400],
                        mr: 1,
                        fontSize: 16,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color={colors.grey[200]}
                      fontWeight="500"
                    >
                      {formatDateTime(group.start_time)}
                    </Typography>
                  </Box>

                  <Divider
                    sx={{
                      bgcolor: colors.grey[700],
                      mb: 2,
                      background: `linear-gradient(90deg, transparent 0%, ${colors.grey[700]} 50%, transparent 100%)`,
                      height: "1px",
                      border: "none",
                    }}
                  />

                  {/* Users Section */}
                  <Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" color={colors.grey[200]}>
                        Members ({group.users.length})
                      </Typography>
                      <Tooltip title="Add Users">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddUserModal(group);
                          }}
                          sx={{
                            color: colors.blueAccent[400],
                            backgroundColor: `${colors.blueAccent[700]}20`,
                            borderRadius: "12px",
                            padding: "8px",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: colors.blueAccent[700],
                              color: colors.grey[100],
                              transform: "scale(1.1)",
                            },
                          }}
                          size="small"
                        >
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* User Avatars Display */}
                    <Box
                      display="flex"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1}
                    >
                      {group.users.length === 0 ? (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          width="100%"
                          py={2}
                          sx={{
                            borderRadius: "12px",
                            backgroundColor: `${colors.grey[800]}30`,
                            border: `1px dashed ${colors.grey[600]}`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color={colors.grey[400]}
                            textAlign="center"
                          >
                            No members yet
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <Box display="flex" sx={{ position: "relative" }}>
                            {group.users.slice(0, 5).map((user, index) => (
                              <Tooltip
                                key={user.id}
                                title={`${user.name} (${user.email})`}
                                arrow
                              >
                                <Avatar
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: colors.blueAccent[500],
                                    border: `2px solid ${colors.primary[400]}`,
                                    marginRight: index === 4 ? 0 : "-8px",
                                    zIndex: 5 - index,
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    transition: "all 0.2s ease",
                                    cursor: "pointer",
                                    "&:hover": {
                                      transform: "translateY(-2px) scale(1.1)",
                                      zIndex: 10,
                                      boxShadow: `0 4px 12px ${colors.blueAccent[800]}`,
                                    },
                                  }}
                                >
                                  {user.username.charAt(0).toUpperCase()}
                                </Avatar>
                              </Tooltip>
                            ))}
                          </Box>

                          {group.users.length > 5 && (
                            <Tooltip
                              title={`${group.users.length - 5} more members`}
                              arrow
                            >
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: `${colors.greenAccent[600]}`,
                                  border: `2px solid ${colors.primary[400]}`,
                                  marginLeft: "-8px",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    transform: "translateY(-2px) scale(1.1)",
                                    zIndex: 10,
                                    boxShadow: `0 4px 12px ${colors.greenAccent[800]}`,
                                  },
                                }}
                              >
                                +{group.users.length - 5}
                              </Avatar>
                            </Tooltip>
                          )}

                          <Box ml="auto">
                            <Tooltip title="View All Members">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Add view all members functionality here
                                  console.log(
                                    "View all members for",
                                    group.name
                                  );
                                }}
                                sx={{
                                  color: colors.grey[400],
                                  fontSize: "12px",
                                  padding: "4px",
                                  "&:hover": {
                                    color: colors.blueAccent[400],
                                    backgroundColor: `${colors.blueAccent[700]}20`,
                                  },
                                }}
                                size="small"
                              >
                                <PersonIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            color: colors.grey[100],
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h4" color={colors.grey[100]}>
            {editingGroup ? "Edit Group" : "Add New Group"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Group Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: colors.grey[100],
                      "& fieldset": { borderColor: colors.grey[700] },
                      "&:hover fieldset": { borderColor: colors.grey[500] },
                    },
                    "& .MuiInputLabel-root": { color: colors.grey[300] },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: colors.grey[300] }}>
                    Status
                  </InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    sx={{
                      color: colors.grey[100],
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.grey[700],
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.grey[500],
                      },
                    }}
                  >
                    <MenuItem value="public">Public</MenuItem>
                    <MenuItem value="private">Private</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) =>
                    setFormData({ ...formData, start_time: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: colors.grey[100],
                      "& fieldset": { borderColor: colors.grey[700] },
                      "&:hover fieldset": { borderColor: colors.grey[500] },
                    },
                    "& .MuiInputLabel-root": { color: colors.grey[300] },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={users} // full user objects
                  getOptionLabel={(option) => option.username}
                  value={
                    users.filter((user) => formData.users.includes(user._id)) // convert IDs to user objects
                  }
                  onChange={(event, newValue) => {
                    const userIds = newValue.map((user) => user._id); // convert back to IDs
                    setFormData({ ...formData, users: userIds });
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.username}
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.username}
                        {...getTagProps({ index })}
                        key={option._id}
                        sx={{
                          color: colors.grey[100],
                          borderColor: colors.blueAccent[500],
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Users"
                      placeholder="Choose users for this group"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: colors.grey[100],
                          "& fieldset": { borderColor: colors.grey[700] },
                          "&:hover fieldset": { borderColor: colors.grey[500] },
                        },
                        "& .MuiInputLabel-root": { color: colors.grey[300] },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiAutocomplete-popupIndicator": {
                      color: colors.grey[300],
                    },
                    "& .MuiAutocomplete-clearIndicator": {
                      color: colors.grey[300],
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} sx={{ color: colors.grey[300] }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
            }}
          >
            {editingGroup ? "Update" : "Create"} Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            color: colors.grey[100],
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h5" color={colors.redAccent[500]}>
            Delete Group
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography color={colors.grey[100]}>
            Are you sure you want to delete the group "{deleteConfirm?.name}"?
            This will remove all users from the group.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirm(null)}
            sx={{ color: colors.grey[300] }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteGroup(deleteConfirm.id)}
            variant="contained"
            sx={{
              backgroundColor: colors.redAccent[600],
              color: colors.grey[100],
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Users to Group Modal */}
      <Dialog
        open={!!addUserModal}
        onClose={() => {
          setAddUserModal(null);
          setSelectedUsersToAdd([]); // Reset on close
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            color: colors.grey[100],
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h5" color={colors.grey[100]}>
            Add Users to "{addUserModal?.name}"
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            options={availableUsers}
            getOptionLabel={(option) => `${option.username}`}
            value={selectedUsersToAdd}
            onChange={(event, newValue) => {
              setSelectedUsersToAdd(newValue);
            }}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBox fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  sx={{ color: colors.grey[300] }}
                />
                {option.username})
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Users to Add"
                placeholder="Choose users to add to this group"
                sx={{
                  mt: 2,
                  "& .MuiOutlinedInput-root": {
                    color: colors.grey[100],
                    "& fieldset": { borderColor: colors.grey[700] },
                    "&:hover fieldset": { borderColor: colors.grey[500] },
                  },
                  "& .MuiInputLabel-root": { color: colors.grey[300] },
                }}
              />
            )}
            sx={{
              "& .MuiAutocomplete-popupIndicator": { color: colors.grey[300] },
              "& .MuiAutocomplete-clearIndicator": { color: colors.grey[300] },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddUserModal(null);
              setSelectedUsersToAdd([]);
            }}
            sx={{ color: colors.grey[300] }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedUsersToAdd.length > 0) {
                handleAddUsersToGroup(addUserModal._id, selectedUsersToAdd);
                setAddUserModal(null);
                setSelectedUsersToAdd([]);
              }
            }}
            variant="contained"
            disabled={selectedUsersToAdd.length === 0}
            sx={{
              backgroundColor: colors.blueAccent[700],
              "&:hover": { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Add Selected Users ({selectedUsersToAdd.length})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserGroups;
